import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["mode", "count", "variance", "varianceControl", "varianceAction", "peopleSelect", "rangeSummaryGroup", "rangeSummary", "openSummary"]
  static values = {
    personOneLabel: String,
    personOtherLabel: String,
    summaryExactTemplate: String,
    summaryRangeTemplate: String,
    openOneTemplate: String,
    openOtherTemplate: String,
    filledTemplate: String,
    overOneTemplate: String,
    overOtherTemplate: String
  }

  connect() {
    this.varianceExpanded = this.varianceValue() > 0
    this.update()
  }

  // Same late-arrival case as assigned-job-people-conflict-preview: the
  // people select can land after connect() has run (a morph mid-edit).
  peopleSelectTargetConnected() {
    this.update()
  }

  update() {
    this.updateVarianceVisibility()
    this.updateSummary()
  }

  updateSummary() {
    if (!this.hasRangeSummaryTarget && !this.hasOpenSummaryTarget) return

    const range = this.currentRange()
    if (!range) {
      this.updateRangeSummaryVisibility(false)
      if (this.hasRangeSummaryTarget) this.setText(this.rangeSummaryTarget, "")
      if (this.hasOpenSummaryTarget) this.setText(this.openSummaryTarget, "")
      return
    }

    const showRangeSummary = range.min !== range.max
    this.updateRangeSummaryVisibility(showRangeSummary)
    if (this.hasRangeSummaryTarget) {
      this.setText(this.rangeSummaryTarget, showRangeSummary ? this.rangeSummaryText(range) : "")
    }
    if (this.hasOpenSummaryTarget) {
      const openSummary = this.openSummary(range, this.selectedPeopleCount())
      this.setText(this.openSummaryTarget, openSummary.text)
      this.openSummaryTarget.dataset.status = openSummary.status
    }
  }

  updateRangeSummaryVisibility(showRangeSummary) {
    this.rangeSummaryGroupTargets.forEach((target) => {
      target.classList.toggle("hidden", !showRangeSummary)
    })
  }

  updateVarianceVisibility() {
    const showVariance = this.varianceExpanded || this.varianceValue() > 0

    this.varianceControlTargets.forEach((target) => {
      target.classList.toggle("hidden", !showVariance)
    })

    this.varianceActionTargets.forEach((target) => {
      target.classList.toggle("hidden", showVariance)
    })
  }

  showVariance(event) {
    event?.preventDefault()

    this.varianceExpanded = true
    if (this.hasVarianceTarget && this.varianceValue() <= 0) {
      this.varianceTarget.value = "1"
    }
    this.update()
  }

  stepNumber(event) {
    event?.preventDefault()

    const input = event.params.field === "variance" ?
      (this.hasVarianceTarget ? this.varianceTarget : null) :
      (this.hasCountTarget ? this.countTarget : null)
    if (!input) return

    const delta = Number(event.params.delta || 0)
    const step = Number(input.getAttribute("step") || 1)
    const min = Number(input.getAttribute("min"))
    const currentValue = this.integerFrom(input.value)
    const steppedValue = currentValue + (delta * step)
    const nextValue = Number.isFinite(min) ? Math.max(min, steppedValue) : steppedValue
    input.value = String(nextValue)

    input.dispatchEvent(new Event("input", {bubbles: true}))
    input.dispatchEvent(new Event("change", {bubbles: true}))
    this.update()
  }

  selectNumber(event) {
    const input = event.currentTarget
    if (!input) return

    window.requestAnimationFrame(() => input.select())
    input.addEventListener("mouseup", this.preventSelectionCollapse, {once: true})
  }

  preventSelectionCollapse(event) {
    event.preventDefault()
  }

  currentRange() {
    if (!this.hasCountTarget) return null

    const target = this.integerFrom(this.countTarget.value)
    if (target <= 0) return null

    const variance = this.varianceValue()
    const mode = this.currentMode()

    if (mode === "plus") {
      return {min: target, max: target + variance}
    }

    if (mode === "minus") {
      return {min: Math.max(target - variance, 0), max: target}
    }

    return {min: Math.max(target - variance, 0), max: target + variance}
  }

  rangeSummaryText({min, max}) {
    if (min === max) {
      return this.format(this.summaryExactTemplateValue, {
        count: min,
        people: this.peopleLabel(min)
      })
    }

    return this.format(this.summaryRangeTemplateValue, {
      min,
      max,
      people: this.personOtherLabelValue
    })
  }

  openSummary({min, max}, selectedCount) {
    if (selectedCount > max) {
      const overCount = selectedCount - max
      return {
        status: "over",
        text: this.format(this.templateForCount(overCount, this.overOneTemplateValue, this.overOtherTemplateValue), {
          selected: selectedCount,
          count: overCount
        })
      }
    }

    const maximumOpenCount = max - selectedCount
    if (maximumOpenCount === 0) {
      return {
        status: "filled",
        text: this.format(this.filledTemplateValue, {selected: selectedCount})
      }
    }

    const minimumOpenCount = Math.max(min - selectedCount, 0)
    const openCount = minimumOpenCount > 0 && minimumOpenCount !== maximumOpenCount ?
      `${minimumOpenCount}-${maximumOpenCount}` :
      maximumOpenCount

    return {
      status: "open",
      text: this.format(this.templateForOpenCount(openCount), {
        selected: selectedCount,
        count: openCount
      })
    }
  }

  currentMode() {
    const checkedMode = this.modeTargets.find((target) => target.checked)

    return checkedMode?.value || "plus_minus"
  }

  selectedPeopleCount() {
    if (!this.hasPeopleSelectTarget) return 0

    return Array.from(this.peopleSelectTarget.selectedOptions)
      .filter((option) => option.value !== "")
      .length
  }

  peopleLabel(count) {
    return count === 1 ? this.personOneLabelValue : this.personOtherLabelValue
  }

  templateForCount(count, oneTemplate, otherTemplate) {
    return count === 1 ? oneTemplate : otherTemplate
  }

  templateForOpenCount(count) {
    return typeof count === "number" && count === 1 ? this.openOneTemplateValue : this.openOtherTemplateValue
  }

  format(template, values) {
    return Object.entries(values).reduce(
      (result, [key, value]) => result.replaceAll(`%{${key}}`, value),
      template || ""
    )
  }

  integerFrom(value) {
    const parsed = Number.parseInt(value, 10)
    return Number.isFinite(parsed) ? parsed : 0
  }

  varianceValue() {
    if (!this.hasVarianceTarget) return 0

    return this.integerFrom(this.varianceTarget.value)
  }

  setText(target, text) {
    if (!target) return

    target.textContent = text
  }
}
